import { GetStaticProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react'
import { CompanySize, ICompanyBase, Language, CompanyOrderBase } from '@/types'
import { getStaticStores, useStores } from '@/utils'
import { MyHead } from '@components-v2/organisms/MyHead'
import { PageHeader } from '@components-v2/molecules/PageHeader'
import { Button2 } from '@components-v2/atoms/Button2'
import { DefaultPageTemplate } from '@components-v2/templates/DefaultPageTemplate'
import { KeywordSearchGroupForCompanies } from '@components-v2/organisms/KeywordSearchGroupForCompanies'
import { StartUpCardList } from '@components-v2/organisms/StartUpCardList'
import { GoogleAdSense } from '@/components/atoms/GoogleAdSense'
import styles from './index.module.scss'
import { ProfileCompletionPromotionPopup } from '../../components/organisms/ProfileCompletionPromotionPopup'
import { SignUpPromotionPopup } from '../../components/organisms/SignUpPromotionPopup'

type Props = {
  staticStartups: ICompanyBase[]
  staticHasNextStartupsPage: boolean
}

const NUM_OF_ITEMS = 20

const Startups: NextPage<Props> = observer(({ staticStartups, staticHasNextStartupsPage }) => {
  const { t } = useTranslation(['angels-vcs-startups'])
  const { companies } = useStores()

  const [searchResults, setSearchResults] = useState<ICompanyBase[]>(
    companies.startups.length > 0 ? companies.startups : staticStartups
  )
  const [searchKeyword, setSearchKeyword] = useState('')
  const [marketIds, setMarketIds] = useState<string[]>([])
  const [companySize, setCompanySize] = useState<CompanySize>(null)
  const [locationIds, setLocationIds] = useState<string[]>([])
  const [roundIds, setRoundIds] = useState<string[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [orderBy, setOrderBy] = useState<CompanyOrderBase>(null)

  const searchHandler = async (
    keyword: string,
    markets: string[],
    size: CompanySize,
    locations: string[],
    rounds: string[],
    companyOrder: CompanyOrderBase
  ) => {
    const result = await companies.fetchStartups({
      searchWord: keyword,
      marketIds: markets,
      companySize: size,
      roundIds: rounds,
      locationIds: locations,
      orderBy: companyOrder,
      limit: NUM_OF_ITEMS,
      shouldRefresh: true,
    })
    setSearchResults(result)
  }

  const fetchStartups = async () => {
    setIsFetching(true)
    const isFirstTimeFetching = staticStartups === searchResults
    await companies.fetchStartups({
      searchWord: searchKeyword,
      marketIds,
      companySize,
      roundIds,
      locationIds,
      orderBy,
      limit: isFirstTimeFetching ? NUM_OF_ITEMS * 2 : NUM_OF_ITEMS, // 初回の追加読み込みは ISR 分も含めて倍読み込む
      shouldRefresh: isFirstTimeFetching, // 初回の追加読み込みは Store の中身をリセットするために shouldRefresh: true
    })
    setSearchResults(companies.startups)
    setIsFetching(false)
  }

  const setSearchValues = async (
    keyword: string,
    markets: string[],
    size: CompanySize,
    locations: string[],
    rounds: string[],
    companyOrder: CompanyOrderBase
  ) => {
    setSearchKeyword(keyword)
    setMarketIds(markets)
    setCompanySize(size)
    setLocationIds(locations)
    setRoundIds(rounds)
    setOrderBy(companyOrder)
    await searchHandler(keyword, markets, size, locations, rounds, companyOrder)
  }

  // ブラウザ側での初期化時に Store に hasNextPage を設定
  useEffect(() => {
    companies.updateHasNextStartupsPage(staticHasNextStartupsPage)
  }, [])

  return (
    <DefaultPageTemplate withContainer={false}>
      <MyHead title={t('スタートアップ一覧')} />
      <div className={styles.container}>
        <div className={styles.heading}>
          <PageHeader title={t('スタートアップ一覧')} />
        </div>
        <div className='container'>
          <div className={styles.searchGroup}>
            <KeywordSearchGroupForCompanies
              search={async (keyword, markets, size, locations, rounds, companyOrder) =>
                setSearchValues(keyword, markets, size, locations, rounds, companyOrder)
              }
            />
          </div>
          <GoogleAdSense slot={process.env.NEXT_PUBLIC_ADSENSE_STARTUP_LIST_SLOT} />
          <div className={styles.content}>
            <StartUpCardList items={searchResults} />
          </div>
          {companies.hasNextStartupsPage && (
            <div className={styles.button}>
              <Button2
                size='md'
                hierarchy='secondary-color'
                componentType='button'
                isLoading={isFetching}
                onClick={fetchStartups}
              >
                {t('+ Load More')}
              </Button2>
            </div>
          )}
        </div>
        <ProfileCompletionPromotionPopup type='investor' />
        <SignUpPromotionPopup type='investor' />
      </div>
    </DefaultPageTemplate>
  )
})

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const { companies, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const staticStartups = await companies.fetchStartups({ limit: NUM_OF_ITEMS, shouldRefresh: true })

  return {
    props: {
      staticStartups,
      staticHasNextStartupsPage: companies.hasNextStartupsPage,
      forcedTheme: 'light',
      ...(await serverSideTranslations(locale, ['global', 'angels-vcs-startups', 'search', 'p'])),
    },
    revalidate: 1,
  }
}

export default Startups
